import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ReactLenis } from "lenis/react";
import { toast } from "sonner";
import {
  Trees,
  Satellite,
  Leaf,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  Loader2,
  Share2,
  Copy,
  BarChart3,
  Wind,
  Download,
  Sparkles,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AOISelector from "@/components/AOISelector";
import AddressSearch from "@/components/AddressSearch";
import { estimateInr, formatINR, formatKm2, bboxFromCoords } from "@/lib/geo";
import { downloadGeoJSON } from "@/lib/geojson";
import { AOI_PRESETS, presetToAoi } from "@/data/aoiPresets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAPABILITIES = [
  { icon: Satellite, title: "Deforestation Alerts", body: "Weekly change detection on your AOI from radar + optical fusion. no cloud blind-spots." },
  { icon: Leaf, title: "Canopy & Biomass", body: "Tree-cover density, canopy height and above-ground biomass tracked over time." },
  { icon: AlertTriangle, title: "Fire & Encroachment", body: "Active-fire overlays and footprint intrusion alerts via webhook or email." },
  { icon: Trees, title: "Carbon Reporting", body: "Audit-ready carbon-stock baselines and loss accounting for MRV-grade reports." },
];

const TIERS = [
  { range: "Up to 1,000 km²", rate: "₹59/km²/mo", label: "Pilot & small AOIs" },
  { range: "1,000. 10,000 km²", rate: "₹49/km²/mo", label: "Regional programmes" },
  { range: "Above 10,000 km²", rate: "₹39/km²/mo", label: "Landscape scale" },
];

// Illustrative carbon-stock density (tC/ha) by rough latitude band.
// Tropical ~ 150 tC/ha, Subtropical ~ 90, Temperate ~ 60.
function carbonDensityForBand(midLat) {
  const abs = Math.abs(midLat);
  if (abs <= 23.5) return 150;
  if (abs <= 35) return 100;
  if (abs <= 50) return 70;
  return 45;
}

function estimateCarbon(aois) {
  if (!aois.length) return null;
  let tC = 0;
  let totalKm2 = 0;
  aois.forEach((a) => {
    const [, s, , n] = a.bbox || bboxFromCoords(a.coords);
    const midLat = (s + n) / 2;
    const dens = carbonDensityForBand(midLat); // tC/ha
    const ha = a.area_km2 * 100; // 1 km² = 100 ha
    tC += dens * ha;
    totalKm2 += a.area_km2;
  });
  const tCO2 = tC * 3.67; // molecular ratio
  // Rough annual loss baseline: 0.6% / year of standing stock (illustrative)
  const annualLossTCO2 = tCO2 * 0.006;
  return { tC, tCO2, annualLossTCO2, totalKm2 };
}

export default function Forests() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [aois, setAois] = useState([]);
  const [months, setMonths] = useState(12);
  const [form, setForm] = useState({ name: "", email: "", organization: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [focusAoi, setFocusAoi] = useState(null);
  const [pin, setPin] = useState(null);

  const applyPreset = (p) => {
    const a = presetToAoi(p);
    setAois((prev) => [...prev, a]);
    setFocusAoi(a);
    setPin(null);
  };

  const handleAddressSelect = (place) => {
    const target = {
      bbox: place.bbox,
      area_km2: 0,
      coords: [[place.bbox[0], place.bbox[1]]],
    };
    setPin({ lon: place.lon, lat: place.lat, label: place.label });
    setFocusAoi(target);
    toast.success(`Centered on ${place.label.split(",").slice(0, 2).join(",")}`);
  };

  const handlePinDrop = async ([lon, lat]) => {
    // Optimistic pin + reverse geocode
    setPin({ lon, lat, label: `${lat.toFixed(3)}°, ${lon.toFixed(3)}°` });
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=12&addressdetails=1`,
        { headers: { Accept: "application/json" } }
      );
      const data = await r.json();
      const name = data?.display_name || `${lat.toFixed(3)}°, ${lon.toFixed(3)}°`;
      setPin({ lon, lat, label: name });
      toast.success(`Pinned ${name.split(",").slice(0, 2).join(",")}`);
    } catch {
      // keep coords label
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load shared draft, if any
  useEffect(() => {
    const draftId = searchParams.get("aoi");
    if (!draftId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/forests/aoi-draft/${draftId}`);
        if (r.data?.aois?.length) {
          setAois(r.data.aois.map((a) => ({ ...a, id: Date.now() + Math.random() })));
          if (r.data.months) setMonths(r.data.months);
          toast.success("Shared AOI loaded");
        }
      } catch {
        toast.error("Shared AOI not found or expired");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalArea = useMemo(() => aois.reduce((s, a) => s + (a.area_km2 || 0), 0), [aois]);
  const price = estimateInr(totalArea, months);
  const carbon = useMemo(() => estimateCarbon(aois), [aois]);

  const share = async () => {
    if (!aois.length) {
      toast.error("Draw at least one AOI to share.");
      return;
    }
    setSharing(true);
    try {
      const payload = {
        aois: aois.map(({ mode, coords, bbox, area_km2 }) => ({ mode, coords, bbox, area_km2 })),
        months,
      };
      const r = await axios.post(`${API}/forests/aoi-draft`, payload);
      const url = `${window.location.origin}/forests?aoi=${r.data.id}`;
      setShareUrl(url);
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard");
      } catch {
        toast.success("Share link ready");
      }
      setSearchParams({ aoi: r.data.id });
    } catch {
      toast.error("Could not create share link");
    } finally {
      setSharing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!aois.length) {
      toast.error("Draw at least one AOI first.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const overallBbox = (() => {
        let w = Infinity, s = Infinity, e2 = -Infinity, n = -Infinity;
        aois.forEach((a) => {
          const [aw, as, ae, an] = a.bbox || bboxFromCoords(a.coords);
          w = Math.min(w, aw); s = Math.min(s, as); e2 = Math.max(e2, ae); n = Math.max(n, an);
        });
        return [w, s, e2, n];
      })();
      await axios.post(`${API}/forests/quote`, {
        ...form,
        mode: aois.length === 1 ? aois[0].mode : "multi",
        area_km2: totalArea,
        months,
        aoi: {
          bbox: overallBbox,
          aois: aois.map(({ mode, coords, bbox, area_km2 }) => ({ mode, coords, bbox, area_km2 })),
        },
      });
      setSubmitted(true);
      toast.success("Quote request sent. Check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="relative min-h-screen bg-arjuna-ink" data-testid="forests-page">
        <Navbar />

        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-10">
          <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60%_55%_at_15%_20%,rgba(34,197,94,0.12),transparent_60%),radial-gradient(50%_45%_at_85%_30%,rgba(0,229,255,0.10),transparent_60%)]" />
          <div className="container-x relative">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-3xl"
            >
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.28em] text-emerald-400">
                <Trees className="h-3.5 w-3.5" /> Forests · AOI Console
              </span>
              <h1 className="mt-6 font-serif text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                Draw your forest. <span className="text-emerald-400">Get a quote in seconds.</span>
              </h1>
              <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">
                Select one or more Areas of Interest on the globe or 2D map. We'll
                size up satellite revisit cadence, deforestation alerts, canopy
                change detection and carbon-stock reporting. then price it
                instantly as a bundle.
              </p>
            </motion.div>
          </div>
        </section>

        {/* AOI + Quote */}
        <section className="relative pb-20">
          <div className="container-x">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <div className="lg:col-span-8">
                {/* Address search */}
                <AddressSearch onSelect={handleAddressSelect} />
                {pin && (
                  <div
                    className="mt-2 flex items-center gap-2 rounded-full border border-[#FF7A1A]/30 bg-[#FF7A1A]/5 px-3 py-1.5 text-[11px] text-slate-200"
                    data-testid="pin-info"
                  >
                    <span className="h-2 w-2 rounded-full bg-[#FF7A1A] animate-pulse" />
                    <span className="truncate">
                      Pinned · <span className="text-[#FFB688]">{pin.label.split(",").slice(0, 2).join(",")}</span>{" "}
                      · {pin.lat.toFixed(3)}°, {pin.lon.toFixed(3)}°
                    </span>
                    <button
                      type="button"
                      onClick={() => setPin(null)}
                      className="ml-auto text-slate-400 hover:text-white"
                      aria-label="Remove pin"
                    >
                      Remove
                    </button>
                  </div>
                )}

                {/* AOI presets */}
                <div className="mb-3 mt-4 flex flex-wrap items-center gap-2" data-testid="aoi-presets">
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.24em] text-emerald-400">
                    <Sparkles className="h-3.5 w-3.5" /> Templates
                  </span>
                  {AOI_PRESETS.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => applyPreset(p)}
                      data-testid={`preset-${p.key}`}
                      className="group inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/5 px-3 py-1.5 text-xs text-slate-200 transition-colors hover:border-emerald-400/60 hover:text-emerald-300"
                    >
                      <span className="font-medium">{p.name}</span>
                      <span className="text-[10px] text-slate-400 group-hover:text-emerald-300/80">
                        {p.sub}
                      </span>
                    </button>
                  ))}
                </div>

                <AOISelector aois={aois} setAois={setAois} focusTarget={focusAoi} pin={pin} onPinDrop={handlePinDrop} />

                <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  {CAPABILITIES.map(({ icon: Icon, title, body }) => (
                    <div
                      key={title}
                      className="rounded-2xl border border-white/10 bg-white/[0.02] p-4"
                    >
                      <Icon className="h-5 w-5 text-emerald-400" />
                      <p className="mt-3 text-sm font-semibold text-white">{title}</p>
                      <p className="mt-1 text-xs leading-relaxed text-slate-400">{body}</p>
                    </div>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-4">
                <div className="sticky top-28 space-y-4">
                  {/* Quote card */}
                  <div
                    className="rounded-3xl border border-emerald-500/25 bg-gradient-to-br from-arjuna-surface to-black/70 p-6 shadow-[0_30px_80px_-40px_rgba(34,197,94,0.4)]"
                    data-testid="quote-card"
                  >
                    <div className="flex items-center justify-between">
                      <p className="overline text-emerald-400">Live Quote</p>
                      <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        {aois.length} AOI{aois.length === 1 ? "" : "s"}
                      </span>
                    </div>

                    <div className="mt-4 rounded-2xl border border-white/10 bg-black/40 p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        Total area (bundled)
                      </p>
                      <p className="mt-1 font-serif text-3xl text-white" data-testid="quote-area">
                        {formatKm2(totalArea)}
                      </p>
                    </div>

                    <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-black/30 p-3">
                      <label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                        Monitoring window
                      </label>
                      <select
                        value={months}
                        onChange={(e) => setMonths(parseInt(e.target.value, 10))}
                        data-testid="quote-months"
                        className="rounded-full border border-white/10 bg-arjuna-ink px-3 py-1.5 text-xs text-white outline-none focus:border-emerald-400"
                      >
                        {[3, 6, 12, 24, 36].map((m) => (
                          <option key={m} value={m}>{m} months</option>
                        ))}
                      </select>
                    </div>

                    <div className="mt-4 space-y-2 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4">
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-slate-400">Monthly estimate</span>
                        <span className="font-mono text-sm text-white">{formatINR(price.monthly)}</span>
                      </div>
                      <div className="flex items-baseline justify-between border-t border-white/10 pt-2">
                        <span className="text-xs text-slate-300">Total · {months} mo</span>
                        <span className="font-serif text-2xl text-emerald-400" data-testid="quote-total">
                          {formatINR(price.total)}
                        </span>
                      </div>
                      <p className="pt-1 text-[11px] text-slate-500">
                        Illustrative. Final quote covers revisit cadence, alerts & MRV reporting.
                      </p>
                    </div>

                    {/* Share AOI + Export */}
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={share}
                        disabled={sharing || !aois.length}
                        data-testid="share-aoi"
                        className="btn-ghost inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                        Share
                      </button>
                      <button
                        type="button"
                        onClick={() => downloadGeoJSON(aois)}
                        disabled={!aois.length}
                        data-testid="export-geojson"
                        className="btn-ghost inline-flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Download className="h-4 w-4" /> GeoJSON
                      </button>
                    </div>
                    {shareUrl && (
                      <div
                        className="mt-2 flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-[11px]"
                        data-testid="share-url"
                      >
                        <span className="truncate text-slate-300">{shareUrl}</span>
                        <button
                          type="button"
                          onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Copied"); }}
                          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-emerald-400 hover:bg-white/5"
                          aria-label="Copy share link"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}

                    {/* Form */}
                    {submitted ? (
                      <div className="mt-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-center" data-testid="quote-submitted">
                        <CheckCircle2 className="mx-auto h-6 w-6 text-emerald-400" />
                        <p className="mt-2 font-serif text-lg text-white">Quote sent.</p>
                        <p className="mt-1 text-xs text-slate-400">
                          Check your inbox. a specialist will follow up within one business day.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={submit} className="mt-5 space-y-3" data-testid="quote-form">
                        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="form-name" className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-emerald-400" />
                        <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" data-testid="form-email" className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-emerald-400" />
                        <input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} placeholder="Organization (optional)" data-testid="form-org" className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-emerald-400" />
                        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Project notes (optional)" rows={2} data-testid="form-notes" className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-sm text-white placeholder-slate-500 outline-none focus:border-emerald-400" />
                        <button type="submit" disabled={submitting || !aois.length} data-testid="form-submit" className="btn-primary w-full justify-center disabled:cursor-not-allowed disabled:opacity-50">
                          {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending…</>) : (<>Request Full Quote<ArrowRight className="h-4 w-4" /></>)}
                        </button>
                        {!aois.length && (
                          <p className="text-center text-[11px] text-slate-500">Draw an AOI on the map to enable submission.</p>
                        )}
                      </form>
                    )}
                  </div>

                  {/* Carbon Reporting Preview */}
                  <CarbonPreview carbon={carbon} />
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section className="relative border-t border-white/10 bg-arjuna-surface/40 py-16">
          <div className="container-x">
            <p className="overline text-emerald-400">Pricing · Illustrative</p>
            <h2 className="mt-3 font-serif text-3xl text-white sm:text-4xl">
              Scale-aware pricing for every programme size.
            </h2>
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
              {TIERS.map((t, i) => (
                <motion.div
                  key={t.range}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="rounded-3xl border border-white/10 bg-white/[0.02] p-6"
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">{t.label}</p>
                  <p className="mt-3 font-serif text-2xl text-white">{t.range}</p>
                  <p className="mt-1 text-sm text-slate-400">{t.rate}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </ReactLenis>
  );
}

function CarbonPreview({ carbon }) {
  const empty = !carbon || carbon.tCO2 <= 0;
  return (
    <div
      className="rounded-3xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/[0.05] to-black/60 p-6"
      data-testid="carbon-preview"
    >
      <div className="flex items-center justify-between">
        <p className="overline text-emerald-400">Carbon · Baseline</p>
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Preview</span>
      </div>
      <h3 className="mt-3 font-serif text-xl text-white">
        What your forest holds. and what's at risk.
      </h3>

      {empty ? (
        <p className="mt-3 text-xs text-slate-400">
          Draw an AOI to preview an illustrative carbon baseline and projected
          annual loss. Final numbers use ground-truth and ESA/NASA biomass
          layers.
        </p>
      ) : (
        <div className="mt-5 space-y-3">
          <Metric
            icon={Leaf}
            label="Above-ground carbon stock"
            value={`${formatCompact(carbon.tC)} tC`}
            sub="Density × area (band-weighted)"
          />
          <Metric
            icon={BarChart3}
            label="CO₂ equivalent"
            value={`${formatCompact(carbon.tCO2)} tCO₂e`}
            sub="× 3.67 molecular ratio"
            accent="#22C55E"
          />
          <Metric
            icon={Wind}
            label="Annual loss (baseline)"
            value={`${formatCompact(carbon.annualLossTCO2)} tCO₂e/yr`}
            sub="0.6% standing-stock projection"
            accent="#F59E0B"
          />
          <p className="pt-1 text-[11px] text-slate-500">
            Illustrative. Baseline uses tropical/sub-tropical density defaults  
            final reporting is MRV-grade on your AOI.
          </p>
        </div>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, accent = "#00E5FF" }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/40 p-3">
      <Icon className="h-5 w-5 shrink-0" style={{ color: accent }} />
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</p>
        <p className="truncate text-base font-semibold text-white">{value}</p>
      </div>
      <span className="shrink-0 text-right text-[10px] text-slate-500">{sub}</span>
    </div>
  );
}

function formatCompact(n) {
  if (!isFinite(n) || n <= 0) return "0";
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(2) + " B";
  if (abs >= 1e6) return (n / 1e6).toFixed(2) + " M";
  if (abs >= 1e3) return (n / 1e3).toFixed(2) + " k";
  return n.toFixed(0);
}
